import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Graph, ArrowRight, ArrowsClockwise, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_COLORS = {
  PERSON: "bg-rose-100 text-rose-700 border-rose-200",
  ORG: "bg-indigo-100 text-indigo-700 border-indigo-200",
  BRAND: "bg-orange-100 text-orange-700 border-orange-200",
  PRODUCT: "bg-emerald-100 text-emerald-700 border-emerald-200",
  PLACE: "bg-blue-100 text-blue-700 border-blue-200",
  DATE: "bg-amber-100 text-amber-700 border-amber-200",
  METRIC: "bg-purple-100 text-purple-700 border-purple-200",
  CONCEPT: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function IngestionGraph({ knowledgeId, ingestion, onReingested }) {
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reingesting, setReingesting] = useState(false);

  const load = async () => {
    if (!knowledgeId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/knowledge/${knowledgeId}/graph`);
      setGraph(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [knowledgeId]);

  const reingest = async () => {
    setReingesting(true);
    try {
      const { data } = await api.post(`/ingest/${knowledgeId}`);
      toast.success(`Re-ingested · ${data.chunks}c · ${data.entities}e · ${data.relationships}r in ${data.ms}ms`);
      await load();
      onReingested?.(data);
    } catch (err) {
      toast.error("Re-ingest failed");
    } finally {
      setReingesting(false);
    }
  };

  const entById = Object.fromEntries((graph?.entities || []).map((e) => [e.id, e]));

  return (
    <div data-testid="ingestion-graph">
      <div className="flex items-center justify-between mb-3">
        <div className="mono-label flex items-center gap-2">
          <Graph size={14} className="text-slate-700" />
          Ingestion graph
        </div>
        <button
          onClick={reingest}
          disabled={reingesting}
          className="btn-sharp btn-secondary text-xs disabled:opacity-50"
          data-testid="reingest-btn"
        >
          {reingesting ? <Spinner size={12} className="animate-spin" /> : <ArrowsClockwise size={12} />}
          {reingesting ? "Re-ingesting…" : "Re-ingest"}
        </button>
      </div>

      {/* Stage summary */}
      {ingestion ? (
        <div className="grid grid-cols-3 gap-2 mb-4">
          <Stat label="Chunks" value={ingestion.chunks ?? "—"} />
          <Stat label="Entities" value={ingestion.entities ?? "—"} />
          <Stat label="Relations" value={ingestion.relationships ?? "—"} />
        </div>
      ) : (
        <div className="border border-dashed border-slate-300 px-3 py-2 mb-4 text-xs text-slate-500 mono-label">
          Not yet ingested · click Re-ingest to run the pipeline
        </div>
      )}

      {/* Pipeline stages strip */}
      {ingestion && (
        <div className="border border-slate-200 bg-slate-50 px-3 py-2 mb-4 font-mono text-xs flex items-center gap-2 flex-wrap">
          <span className="mono-label">stages</span>
          <span className="px-2 py-0.5 bg-white border border-slate-200">① extract</span>
          <ArrowRight size={10} className="text-slate-400" />
          <span className="px-2 py-0.5 bg-white border border-slate-200">② chunk·overlap</span>
          <ArrowRight size={10} className="text-slate-400" />
          <span className="px-2 py-0.5 bg-white border border-slate-200">③ entities</span>
          <ArrowRight size={10} className="text-slate-400" />
          <span className="px-2 py-0.5 bg-white border border-slate-200">④ embed</span>
          <ArrowRight size={10} className="text-slate-400" />
          <span className="px-2 py-0.5 bg-white border border-slate-200">⑤ graph</span>
          <span className="ml-auto text-slate-400">{ingestion.ms}ms</span>
        </div>
      )}

      {loading && <div className="mono-label pulse-soft">Loading graph…</div>}

      {graph && graph.entities.length > 0 && (
        <>
          <div className="mb-2 mono-label">Entities ({graph.entities.length})</div>
          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="graph-entities">
            {graph.entities.slice(0, 60).map((e) => (
              <span
                key={e.id}
                className={`text-xs font-mono px-2 py-1 border ${TYPE_COLORS[e.type] || TYPE_COLORS.CONCEPT}`}
                title={`${e.type} · ${e.mention_count || 1} mentions`}
                data-testid={`graph-entity-${e.id}`}
              >
                <span className="opacity-60 mr-1">{e.type[0]}</span>
                {e.name}
              </span>
            ))}
            {graph.entities.length > 60 && (
              <span className="text-xs text-slate-500 mono-label">+{graph.entities.length - 60} more</span>
            )}
          </div>

          {graph.relationships.length > 0 && (
            <>
              <div className="mb-2 mono-label">Relationships ({graph.relationships.length})</div>
              <div className="space-y-1 max-h-48 overflow-y-auto" data-testid="graph-relations">
                {graph.relationships.slice(0, 40).map((r) => (
                  <div key={r.id} className="font-mono text-xs flex items-center gap-2 px-2 py-1 border border-slate-100 hover:bg-slate-50" data-testid={`graph-relation-${r.id}`}>
                    <span className="text-slate-900 truncate">{entById[r.from_id]?.name || "?"}</span>
                    <span className="px-1.5 py-0.5 bg-slate-900 text-white text-[10px]">{r.type}</span>
                    <span className="text-slate-900 truncate">{entById[r.to_id]?.name || "?"}</span>
                  </div>
                ))}
                {graph.relationships.length > 40 && (
                  <div className="mono-label text-slate-500">+{graph.relationships.length - 40} more</div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {graph && graph.entities.length === 0 && !loading && (
        <div className="text-sm text-slate-500">No entities extracted yet for this entry.</div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-slate-200 px-3 py-2 bg-white">
      <div className="mono-label">{label}</div>
      <div className="font-display text-2xl text-slate-900">{value}</div>
    </div>
  );
}
